import { useCallback, useState, useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs'

export const RANGE_OPTIONS = [
  { label: '30D', full: '30 Days', value: '30' },
  { label: '90D', full: '90 Days', value: '90' },
  { label: '6M', full: '6 Months', value: '180' },
  { label: '12M', full: '12 Months', value: '365' },
  { label: 'All', full: 'All Time', value: 'all' },
  { label: 'Custom', full: 'Custom Range', value: 'custom' },
]

interface StoredDateRange {
  range: string
  customStart: string | null
  customEnd: string | null
}

const storageKeyFor = (key: string) => `dateRange:${key}`

const loadStored = (key: string): StoredDateRange | null => {
  try {
    const raw = sessionStorage.getItem(storageKeyFor(key))
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const saveStored = (key: string, data: StoredDateRange) => {
  try {
    sessionStorage.setItem(storageKeyFor(key), JSON.stringify(data))
  } catch {}
}

export const useDateRange = (storageKey: string, defaultRange: string = '30') => {
  const stored = loadStored(storageKey)

  const [range, setRange] = useState<string>(stored?.range ?? defaultRange)
  const [customStart, setCustomStart] = useState<Dayjs | null>(
    stored?.customStart ? dayjs(stored.customStart) : null
  )
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(
    stored?.customEnd ? dayjs(stored.customEnd) : null
  )

  useEffect(() => {
    saveStored(storageKey, {
      range,
      customStart: customStart ? customStart.format('YYYY-MM-DD') : null,
      customEnd: customEnd ? customEnd.format('YYYY-MM-DD') : null,
    })
  }, [storageKey, range, customStart, customEnd])

  const handleRangeChange = (_event: React.MouseEvent<HTMLElement>, value: string | null) => {
    if (!value) return
    setRange(value)
    if (value !== 'custom') {
      setCustomStart(null)
      setCustomEnd(null)
    }
  }

  const buildQuery = useCallback(() => {
    if (range === 'custom') {
      if (!customStart) return null
      const params = new URLSearchParams({ range: 'custom', startDate: customStart.format('YYYY-MM-DD') })
      if (customEnd) params.append('endDate', customEnd.format('YYYY-MM-DD'))
      return params.toString()
    }
    return `range=${range}`
  }, [range, customStart, customEnd])

  return {
    range,
    setRange,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
    handleRangeChange,
    buildQuery,
  }
}